<script setup lang="ts">
import { ref, watch, onUnmounted } from 'vue';
import { useEditor, EditorContent } from '@tiptap/vue-3';
import sanitize from '@/lib/sanitize';
import { DefaultExtension, defaultExtensions, ActionPayloads, Level } from '@/interfaces/TipTap';

export interface TipTapExposedMethods {
  handleAction: (action: string, payload?: any) => void;
  isActive: (type: string) => boolean;
  getContent: () => string;
  focus: () => void;
}

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    extensions?: DefaultExtension[];
  }>(),
  {
    extensions: () => defaultExtensions,
  }
);

const content = defineModel<string>({ required: true });

// Emit focus and blur events
const emit = defineEmits<{
  focus: [event: Event];
  blur: [event: Event];
}>();

const editorRef = ref();
// --- TipTap editor ---
const editor = useEditor({
  extensions: props.extensions,
  content: sanitize(content.value || ''),
  editable: !props.disabled,
  onUpdate({ editor: ed }) {
    // Update parent content
    const newContent = ed.getHTML();
    if (newContent !== content.value && !props.disabled) {
      content.value = newContent;
    }
  },
  onFocus({ event }) {
    // Emit focus event when editor gains focus
    emit('focus', event);
  },
  onBlur({ event }) {
    // Emit blur event when editor loses focus
    // emit('blur', event);
  },
});

// --- Expose toolbar actions to parent ---
const handleAction = <T extends string>(
  action: T,
  payload?: T extends keyof ActionPayloads ? ActionPayloads[T] : any
) => {
  if (!editor.value) return;

  const chain = editor.value.chain().focus();

  // Special cases that need custom logic
  const specialCases = {
    alignLeft: () => {
      if (editor.value?.isActive({ textAlign: 'left' })) {
        // Already left aligned - remove alignment
        chain.unsetTextAlign().run();
      } else {
        // Not left aligned - apply left alignment
        chain.setTextAlign('left').run();
      }
    },

    alignCenter: () => {
      if (editor.value?.isActive({ textAlign: 'center' })) {
        // Already center aligned - remove alignment
        chain.unsetTextAlign().run();
      } else {
        // Not center aligned - apply center alignment
        chain.setTextAlign('center').run();
      }
    },

    alignRight: () => {
      if (editor.value?.isActive({ textAlign: 'right' })) {
        // Already right aligned - remove alignment
        chain.unsetTextAlign().run();
      } else {
        // Not right aligned - apply right alignment
        chain.setTextAlign('right').run();
      }
    },
    heading: () => {
      const level = Number(payload);
      if (!level) {
        chain.setParagraph().run();
        return;
      }

      if (![1, 2, 3, 4, 5, 6].includes(level)) {
        return;
      }

      chain.toggleHeading({ level: level as Level }).run();
    },
    setLink: () => {
      const url = window.prompt('URL', payload as string);
      if (url) {
        chain.extendMarkRange('link').setLink({ href: url }).run();
      }
    },
  };

  // Handle special cases
  if (action in specialCases) {
    (specialCases as any)[action]();
    return;
  }

  // Generic command execution
  executeGenericCommand(chain, action, payload);
};

// Helper function for generic commands
const executeGenericCommand = (chain: any, action: string, payload?: any) => {
  try {
    // Handle text align commands specifically if needed
    if (action === 'setTextAlign') {
      chain.setTextAlign(payload).run();
      return;
    }

    const command = chain[action];

    if (typeof command === 'function') {
      if (payload !== undefined) {
        // For commands with parameters
        command.call(chain, payload).run();
      } else {
        // For commands without parameters
        command.call(chain).run();
      }
    } else {
      console.warn(`TipTap command "${action}" not found or not a function`);
    }
  } catch (error) {
    console.error(`Error executing TipTap command "${action}":`, error);
  }
};

const isActive = (type: string) => {
  if (!editor.value) return false;

  try {
    switch (type) {
      case 'bold':
        return editor.value.isActive('bold');
      case 'italic':
        return editor.value.isActive('italic');
      case 'underline':
        return editor.value.isActive('underline');
      case 'strike':
        return editor.value.isActive('strike');
      case 'bulletList':
        return editor.value.isActive('bulletList');
      case 'orderedList':
        return editor.value.isActive('orderedList');
      case 'blockquote':
        return editor.value.isActive('blockquote');
      case 'codeBlock':
        return editor.value.isActive('codeBlock');
      case 'heading1':
        return editor.value.isActive('heading', { level: 1 });
      case 'heading2':
        return editor.value.isActive('heading', { level: 2 });
      case 'heading3':
        return editor.value.isActive('heading', { level: 3 });
      case 'heading4':
        return editor.value.isActive('heading', { level: 4 });
      case 'heading5':
        return editor.value.isActive('heading', { level: 5 });
      case 'heading6':
        return editor.value.isActive('heading', { level: 6 });
      case 'alignLeft':
        return editor.value.isActive({ textAlign: 'left' });
      case 'alignCenter':
        return editor.value.isActive({ textAlign: 'center' });
      case 'alignRight':
        return editor.value.isActive({ textAlign: 'right' });
      case 'link':
        return editor.value.isActive('link');
      default:
        return false;
    }
  } catch (error) {
    console.warn(`Error checking active state for ${type}:`, error);
    return false;
  }
};

// --- Get current content ---
const getContent = () => {
  return editor.value?.getHTML() || '';
};

// --- Watch for content changes from parent ---
watch(content, (newContent) => {
  if (newContent !== editor.value?.getHTML()) {
    editor.value?.commands.setContent(newContent || '');
  }
});

watch(
  () => props.disabled,
  (newDisabled) => {
    if (editor.value) {
      editor.value.setEditable(!newDisabled);
      if (newDisabled) {
        editor.value.commands.blur();
      }
    }
  },
  { immediate: true } // Run immediately in case prop changes after component mount
);

// --- Cleanup ---
onUnmounted(() => {
  editor.value?.destroy();
});

const focus = () => {
  if (editor.value) {
    editor.value.commands.focus();
  }
};

// --- Expose functions to parent ---
defineExpose<TipTapExposedMethods>({ handleAction, isActive, getContent, focus });
</script>

<template>
  <EditorContent ref="editorRef" :editor="editor" class="tw-flex tw-flex-col tw-h-full tw-p-3 Editor" />
</template>

<style>
.Editor,
.tiptap.ProseMirror {
  flex: 1;
  min-height: 0;
  height: 100%;
  max-height: calc(100dvh - 85px);
  display: flex;
  flex-direction: column;
}
</style>
